import React, { useState } from 'react';
import { Employee, TeamPerformanceData, BudgetData } from '../contexts/AppContext.tsx';
import { EmployeePerformanceData } from '../types/employeePerformance.ts';
import { calculateEmployeePerformance, getDefaultTeamPerformance } from '../utils/employeePerformanceUtils.ts';
import { useEmployeePerformanceExporter } from '../hooks/useEmployeePerformanceExporter.ts';
import { shouldMaskDirectorData, isSelfEmployee } from '../utils/teamPerformanceUtils.ts';
import { POSITION_SORT_ORDER } from '../utils/payrollEmployeeUtils.ts';

interface EmployeePerformanceTableProps {
  employees: Employee[];
  teamPerformanceData: Record<string, TeamPerformanceData>;
  userRole?: string;
  userTeamName?: string | null;
  /** 로그인 계정에 연결된 직원 id — 실장 본인 급여 열람 판정용 (없으면 종전대로 admin만) */
  userEmployeeId?: string;
  budgetData?: BudgetData;
  useRealTimeIncentive?: boolean;
  selectedMonth?: string; // 월별 인센티브 비율 해석용 (YYYY-MM)
}

// 직원 데이터 정렬 함수
const sortEmployees = (employees: EmployeePerformanceData[]): EmployeePerformanceData[] => {
  return [...employees].sort((a, b) => {
    // 1. 팀 순서 비교
    if (a.teamName !== b.teamName) {
      return a.teamName.localeCompare(b.teamName);
    }
    
    // 2. 직책 순서 비교 (팀장 > 팀원) - 단일 정의 사용
    const aPositionOrder = POSITION_SORT_ORDER[a.position] ?? 2;
    const bPositionOrder = POSITION_SORT_ORDER[b.position] ?? 2;
    
    if (aPositionOrder !== bPositionOrder) {
      return aPositionOrder - bPositionOrder;
    }
    
    // 3. 팀원인 경우 기본급 순서 (높은 순)
    if (a.position === '팀원' && b.position === '팀원') {
      return b.baseSalary - a.baseSalary;
    }
    
    // 4. 그 외의 경우 이름 순서
    return a.employeeName.localeCompare(b.employeeName);
  });
};

const EmployeePerformanceTable: React.FC<EmployeePerformanceTableProps> = ({
  employees,
  teamPerformanceData,
  userRole,
  userTeamName,
  userEmployeeId,
  budgetData,
  useRealTimeIncentive = true,
  selectedMonth
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  // 🔧 세금·실지급액은 1회 확인 후 일괄 표시/숨김 토글 (셀 단위 반복 확인 제거)
  const [showSensitive, setShowSensitive] = useState(false);

  // 엑셀 내보내기 훅
  const { exportToExcel, isExporting, error: exportError } = useEmployeePerformanceExporter();
  
  // 직원별 성과 정렬 상태
  const [employeeSortConfig, setEmployeeSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // 마스킹 여부 확인 함수
  const shouldMaskData = (teamName: string, position?: string, employeeId?: string | number): boolean => {
    // 실장 데이터는 admin과 '본인'만 볼 수 있음
    if (position && shouldMaskDirectorData(position, userRole, isSelfEmployee(userEmployeeId, employeeId))) return true;
    // 기존: employee 역할의 타팀 마스킹
    if (userRole !== 'employee') return false;
    return userTeamName !== teamName;
  };

  // 마스킹된 값 반환 함수
  const getMaskedValue = (value: any, teamName: string, isNumeric: boolean = true, position?: string, employeeId?: string | number): string => {
    if (shouldMaskData(teamName, position, employeeId)) {
      return '****';
    }
    return isNumeric ? value.toLocaleString() : value;
  };

  // 직원별 성과 데이터 계산
  const employeePerformanceData = employees.map(emp =>
    calculateEmployeePerformance(
      emp,
      teamPerformanceData[emp.team] || getDefaultTeamPerformance(emp.team),
      employees,
      budgetData,
      useRealTimeIncentive,
      teamPerformanceData,
      selectedMonth
    )
  );

  // 팀별 총 인센티브 계산 (배분 비율 표시용)
  const teamIncentiveTotals = React.useMemo(() => {
    const totals: Record<string, number> = {};
    employeePerformanceData.forEach((performance) => {
      if (!totals[performance.teamName]) {
        totals[performance.teamName] = 0;
      }
      totals[performance.teamName] += performance.incentive || 0;
    });
    return totals;
  }, [employeePerformanceData]);

  // 직원 데이터 정렬 함수
  const sortEmployeeData = (data: any[], key: string, direction: 'asc' | 'desc') => {
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // 특별한 경우 처리
      if (key === 'performanceMetrics.teamAchievementRate') {
        aValue = a.performanceMetrics?.teamAchievementRate || 0;
        bValue = b.performanceMetrics?.teamAchievementRate || 0;
      }
      
      // 문자열인 경우
      if (typeof aValue === 'string') {
        return direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // 숫자인 경우
      return direction === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    });
  };

  // 정렬 핸들러 (팀 테이블과 동일한 3상태: desc → asc → 해제)
  const handleEmployeeSort = (key: string) => {
    let direction: 'asc' | 'desc' | null = 'desc';

    if (employeeSortConfig && employeeSortConfig.key === key) {
      if (employeeSortConfig.direction === 'desc') {
        direction = 'asc';
      } else if (employeeSortConfig.direction === 'asc') {
        direction = null;
      }
    }

    if (direction === null) {
      setEmployeeSortConfig(null);
    } else {
      setEmployeeSortConfig({ key, direction });
    }
  };

  // 정렬 아이콘 렌더링
  const renderSortIcon = (key: string) => {
    if (!employeeSortConfig || employeeSortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return employeeSortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // 정렬된 데이터 (기본 정렬 + 사용자 정렬)
  const sortedEmployeePerformanceData = React.useMemo(() => {
    // 먼저 기본 정렬 적용
    const basicSorted = sortEmployees(employeePerformanceData);
    
    // 사용자가 선택한 정렬이 있으면 추가 적용
    if (!employeeSortConfig) return basicSorted;
    
    return sortEmployeeData(basicSorted, employeeSortConfig.key, employeeSortConfig.direction);
  }, [employeePerformanceData, employeeSortConfig]);

  // 달성률에 따른 색상 결정
  const getAchievementColor = (rate: number): string => {
    if (rate >= 1) return 'text-green-600';
    if (rate >= 0.8) return 'text-yellow-600';
    return 'text-red-600';
  };

  // 세금·실지급액 표시 토글 (첫 표시 시에만 경고 모달 1회)
  const handleSensitiveToggle = () => {
    if (showSensitive) {
      setShowSensitive(false);
    } else {
      setShowWarningModal(true);
    }
  };

  const handleWarningConfirm = () => {
    setShowWarningModal(false);
    setShowSensitive(true);
  };

  const handleWarningCancel = () => {
    setShowWarningModal(false);
  };

  // 엑셀 다운로드 핸들러
  const handleExcelDownload = async () => {
    try {
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD 형식

      await exportToExcel({
        employeePerformanceData: sortedEmployeePerformanceData,
        currentDate,
        userRole,
        userTeamName,
        userEmployeeId
      });
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      // 에러 처리는 훅 내에서 처리되므로 추가 처리 불필요
    }
  };

  // 민감정보(세금·실지급액) 셀 렌더링 — 토글 상태에 따라 값 또는 '숨김' 표시
  const renderDataCell = (
    employeeId: number,
    dataType: string,
    value: any,
    teamName: string,
    className: string = "text-gray-900",
    suffix: string = "원",
    position?: string
  ) => {
    if (shouldMaskData(teamName, position, employeeId)) {
      return <span className="text-gray-500">****</span>;
    }

    if (showSensitive) {
      return <span className={className}>{(value || 0).toLocaleString()}{suffix}</span>;
    }

    return <span className="text-gray-500">숨김</span>;
  };

  // 합계 행용 데이터 셀 렌더링 함수
  const renderSummaryDataCell = (
    dataType: string,
    value: any,
    className: string = "text-gray-900",
    suffix: string = "원"
  ) => {
    if (showSensitive) {
      return <span className={className}>{(value || 0).toLocaleString()}{suffix}</span>;
    }

    return <span className="text-gray-500">숨김</span>;
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-700">직원별 성과 현황</h2>
        <div className="flex items-center space-x-3">
          {useRealTimeIncentive && budgetData && (
            <div className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded">
              실시간 인센티브 계산
            </div>
          )}
          <button
            type="button"
            onClick={handleSensitiveToggle}
            className="px-3 py-2 text-sm font-medium rounded-lg transition-colors bg-gray-200 hover:bg-gray-300 text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
          >
            {showSensitive ? '세금·실지급액 숨기기' : '세금·실지급액 표시'}
          </button>
          <button
            onClick={handleExcelDownload}
            disabled={isExporting || sortedEmployeePerformanceData.length === 0}
            className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              isExporting || sortedEmployeePerformanceData.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                엑셀 생성 중...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                엑셀 다운로드
              </>
            )}
          </button>
        </div>
      </div>

      {/* 엑셀 다운로드 에러 메시지 */}
      {exportError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-red-700">
              엑셀 다운로드 실패: {exportError}
            </p>
          </div>
        </div>
      )}

      {/* 경고 모달 */}
      {showWarningModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sensitive-warning-title"
          onKeyDown={(e) => { if (e.key === 'Escape') handleWarningCancel(); }}
        >
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 id="sensitive-warning-title" className="text-lg font-medium text-gray-900">
                  주의사항
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                대략적인 수령액 확인용으로만 사용하시고, 정확한 금액은 실제 급여명세서를 확인바랍니다.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleWarningCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                autoFocus
                onClick={handleWarningConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {([
                { key: 'employeeName', label: '이름', align: 'left' },
                { key: 'teamName', label: '팀', align: 'left' },
                { key: 'position', label: '직책', align: 'left' },
                { key: 'baseSalary', label: '기본급', align: 'right' },
                { key: 'payrollAllowances', label: '수당(초과+개별)', align: 'right' },
                { key: 'incentive', label: '인센티브', align: 'right' },
                { key: 'payrollTotalSalary', label: '급여관리 총급여', align: 'right' },
                { key: 'tax', label: '세금', align: 'right' },
                { key: 'netSalary', label: '실지급액', align: 'right' },
                { key: 'performanceMetrics.teamAchievementRate', label: '팀 달성률', align: 'left' },
              ] as const).map(({ key, label, align }) => (
                <th key={key} className="p-0 text-xs font-medium text-gray-700 uppercase tracking-wider">
                  <button
                    type="button"
                    onClick={() => handleEmployeeSort(key)}
                    className={`w-full px-4 py-3 flex items-center hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${align === 'right' ? 'justify-end' : ''}`}
                  >
                    {label}
                    {renderSortIcon(key)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedEmployeePerformanceData.map((performance) => (
              <tr key={performance.employeeId} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {getMaskedValue(performance.employeeName, performance.teamName, false, undefined, performance.employeeId)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {performance.teamName}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {getMaskedValue(performance.position, performance.teamName, false, performance.position, performance.employeeId)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                  {shouldMaskData(performance.teamName, performance.position, performance.employeeId) ? <span className="text-gray-500">****</span> : `${(performance.baseSalary || 0).toLocaleString()}원`}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                  {shouldMaskData(performance.teamName, performance.position, performance.employeeId) ? <span className="text-gray-500">****</span> : (() => {
                    const employee = employees.find(emp => emp.id === performance.employeeId);
                    if (!employee) return `${(performance.payrollAllowances || 0).toLocaleString()}원`;

                    const overtimeAmount = employee.overtime || 0;
                    const individualAmount = employee.individual || 0;
                    const overtimeMinutes = employee.overtimeMinutes || 0;

                    if (overtimeAmount > 0 && individualAmount > 0) {
                      return (
                        <div className="text-right">
                          <div className="font-medium">
                            {(overtimeAmount + individualAmount).toLocaleString()}원
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {overtimeAmount.toLocaleString()}원({overtimeMinutes}분)+{individualAmount.toLocaleString()}원
                          </div>
                        </div>
                      );
                    } else if (overtimeAmount > 0) {
                      return (
                        <div className="text-right">
                          <div className="font-medium">
                            {overtimeAmount.toLocaleString()}원
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {overtimeAmount.toLocaleString()}원({overtimeMinutes}분)
                          </div>
                        </div>
                      );
                    } else if (individualAmount > 0) {
                      return `${individualAmount.toLocaleString()}원`;
                    }
                    return '0원';
                  })()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                  {shouldMaskData(performance.teamName, performance.position, performance.employeeId) ? <span className="text-gray-500">****</span> : (
                    <span className={useRealTimeIncentive ? 'font-semibold' : ''}>
                      {(performance.incentive || 0).toLocaleString()}원
                      {teamIncentiveTotals[performance.teamName] > 0 && performance.incentive > 0 && (
                        <span className="text-xs text-gray-500 ml-1">
                          ({((performance.incentive / teamIncentiveTotals[performance.teamName]) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums text-gray-900">
                  {shouldMaskData(performance.teamName, performance.position, performance.employeeId) ? <span className="text-gray-500">****</span> : `${(performance.payrollTotalSalary || 0).toLocaleString()}원`}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                  {renderDataCell(performance.employeeId, 'tax', performance.tax, performance.teamName, 'text-gray-900', '원', performance.position)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right tabular-nums">
                  {renderDataCell(performance.employeeId, 'netSalary', performance.netSalary, performance.teamName, 'text-gray-900 font-medium', '원', performance.position)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {shouldMaskData(performance.teamName, performance.position, performance.employeeId) ? (
                    <span className="text-gray-500">****</span>
                  ) : (
                    <div className="flex items-center">
                      <span className={`font-medium ${getAchievementColor(performance.performanceMetrics.teamAchievementRate)}`}>
                        {(performance.performanceMetrics.teamAchievementRate * 100).toFixed(1)}%
                      </span>
                      <div className="ml-2 w-16 bg-gray-200 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full ${
                            performance.performanceMetrics.teamAchievementRate >= 1 
                              ? 'bg-green-600' 
                              : performance.performanceMetrics.teamAchievementRate >= 0.8 
                                ? 'bg-yellow-600' 
                                : 'bg-red-600'
                          }`}
                          style={{ width: `${Math.min(performance.performanceMetrics.teamAchievementRate * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {/* 합계 행 */}
            {sortedEmployeePerformanceData.length > 0 && (
              <tr className="bg-blue-50 font-semibold">
                <td colSpan={3} className="px-4 py-3 text-right text-sm text-gray-900">
                  {userRole === 'employee' ? `${userTeamName} 팀 합계:` : '합계:'}
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900">
                  {sortedEmployeePerformanceData
                    .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                    .reduce((sum, p) => sum + p.baseSalary, 0).toLocaleString()}원
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900">
                  {sortedEmployeePerformanceData
                    .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                    .reduce((sum, p) => sum + p.payrollAllowances, 0).toLocaleString()}원
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900">
                  {sortedEmployeePerformanceData
                    .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                      .reduce((sum, p) => sum + p.incentive, 0).toLocaleString()}원
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900">
                  {sortedEmployeePerformanceData
                    .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                    .reduce((sum, p) => sum + p.payrollTotalSalary, 0).toLocaleString()}원
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">
                  {renderSummaryDataCell('tax',
                    sortedEmployeePerformanceData
                    .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                      .reduce((sum, p) => sum + p.tax, 0),
                    'text-gray-900'
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">
                  {renderSummaryDataCell('netSalary',
                    sortedEmployeePerformanceData
                    .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                      .reduce((sum, p) => sum + p.netSalary, 0),
                    'text-gray-900'
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  {/* 전체 평균 달성률 */}
                  {(() => {
                    // 팀별로 고유한 달성률만 수집
                    const uniqueTeamRates = new Map<string, number>();
                    sortedEmployeePerformanceData
                      .filter(p => userRole !== 'employee' || p.teamName === userTeamName)
                      .forEach(p => {
                        if (!uniqueTeamRates.has(p.teamName)) {
                          uniqueTeamRates.set(p.teamName, p.performanceMetrics.teamAchievementRate);
                        }
                      });
                    
                    const avgRate = uniqueTeamRates.size > 0 
                      ? Array.from(uniqueTeamRates.values()).reduce((sum, rate) => sum + rate, 0) / uniqueTeamRates.size
                      : 0;
                    
                    return (
                      <div className="flex items-center">
                        <span className={`font-medium ${getAchievementColor(avgRate)}`}>
                          평균 {(avgRate * 100).toFixed(1)}%
                        </span>
                        <div className="ml-2 w-16 bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full ${
                              avgRate >= 1 
                                ? 'bg-green-600' 
                                : avgRate >= 0.8 
                                  ? 'bg-yellow-600' 
                                  : 'bg-red-600'
                            }`}
                            style={{ width: `${Math.min(avgRate * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })()}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmployeePerformanceTable;